import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ElementType,
  type ReactNode,
} from 'react';

/* ------------------------------------------------------------------ */
/*  STAMP FIT 디지털 커리어 월렛 인트로                                  */
/*  원본 standalone HTML(wallet3_cleaned.html)의 색·치수를 그대로 재현   */
/*  + 스크롤 리빌/플로팅 블롭/CTA 글로우 애니메이션.                     */
/*  인증서 보관함(CertificateWallet) 위에 헤더 인트로로 렌더된다.        */
/* ------------------------------------------------------------------ */

const RED = '#e33b3b';
const RED_DEEP = '#d72f2f';

/* 스크롤 리빌 래퍼 (IntersectionObserver, prefers-reduced-motion 가드) */
type RevealVariant = 'up' | 'right' | 'scale' | 'fade';

const VARIANT_HIDDEN: Record<RevealVariant, string> = {
  up: 'opacity-0 translate-y-10',
  right: 'opacity-0 translate-x-12',
  scale: 'opacity-0 scale-95',
  fade: 'opacity-0',
};

function Reveal({
  children,
  className = '',
  style,
  variant = 'up',
  delay = 0,
  as: Tag = 'div' as ElementType,
}: {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  variant?: RevealVariant;
  delay?: number;
  as?: ElementType;
}) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <Tag
      ref={ref as never}
      style={{ transitionDelay: `${delay}ms`, ...style, willChange: visible ? 'auto' : 'transform, opacity' }}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] ${
        visible ? 'opacity-100 translate-x-0 translate-y-0 scale-100' : VARIANT_HIDDEN[variant]
      } ${className}`}
    >
      {children}
    </Tag>
  );
}

const FEATURES = [
  {
    num: '01',
    title: '디지털 인증서 보관',
    desc: '수료증, 자격 인증서, 워크샵 참여 인증서를 한 곳에서 안전하게 관리합니다.',
  },
  {
    num: '02',
    title: 'QR 기반 인증 확인',
    desc: '각 인증서에 고유 QR 코드를 연결하여 발급 정보와 진위 여부를 확인합니다.',
  },
  {
    num: '03',
    title: '교육 이력 자동 정리',
    desc: 'STAMP FIT을 통해 수료한 교육 이력이 나의 월렛에 자동으로 누적됩니다.',
  },
  {
    num: '04',
    title: '간편한 구직 활동',
    desc: 'STAMP FIT 전용 이력서인 CAREER PASSPORT에 교육 이력이 자동 연동되어 채용 지원이 간편합니다.',
  },
];

export default function WalletIntro({ children }: { children?: ReactNode }) {
  return (
    <div
      className="overflow-hidden"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Pretendard", "Segoe UI", "Noto Sans KR", sans-serif',
        color: '#171717',
        lineHeight: 1.6,
        WebkitFontSmoothing: 'auto',
        MozOsxFontSmoothing: 'auto',
        background: '#f7f8fb',
      }}
    >
      <style>{`
        @keyframes wl-blob { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(30px,24px) scale(1.14); } }
        @keyframes wl-glow { 0%,100% { transform: scale(1); opacity: 0.8; } 50% { transform: scale(1.18); opacity: 1; } }
        @keyframes wl-pulse { 0% { box-shadow: 0 0 0 0 rgba(227,59,59,0.4); } 70% { box-shadow: 0 0 0 9px rgba(227,59,59,0); } 100% { box-shadow: 0 0 0 0 rgba(227,59,59,0); } }
        .wl-blob { animation: wl-blob 17s ease-in-out infinite; }
        .wl-glow { animation: wl-glow 7s ease-in-out infinite; }
        .wl-dot { animation: wl-pulse 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .wl-blob, .wl-glow, .wl-dot { animation: none !important; }
        }
      `}</style>

      {/* ===== HERO ===== */}
      <section
        className="relative overflow-hidden pb-[100px] pt-[72px]"
        style={{
          background:
            'radial-gradient(circle at top left, rgba(227,59,59,0.14), transparent 35%), linear-gradient(180deg, #ffffff 0%, #f7f8fb 100%)',
        }}
      >
        {/* 떠다니는 배경 블롭 */}
        <div
          className="wl-blob pointer-events-none absolute -left-[120px] -top-[160px] -z-0 h-[520px] w-[520px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(227,59,59,0.16), transparent 65%)' }}
        />

        <div className="relative z-10 mx-auto grid w-[min(1120px,100%)] items-center gap-[56px] lg:grid-cols-[1.05fr_0.95fr]">
          {/* 좌측 카피 */}
          <div>
            <Reveal variant="fade">
              <span
                className="mb-6 inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[14px] font-extrabold"
                style={{ background: '#fff1f1', color: RED_DEEP }}
              >
                <span className="wl-dot h-2 w-2 rounded-full" style={{ background: RED_DEEP }} />
                Digital Career Wallet
              </span>
            </Reveal>

            <Reveal variant="up" delay={80}>
              <h1
                className="mb-[26px] font-black"
                style={{
                  fontSize: 'clamp(42px, 6vw, 76px)',
                  lineHeight: 1.05,
                  letterSpacing: '-0.07em',
                }}
              >
                피트니스 커리어를
                <br />
                하나의 <span style={{ color: RED }}>월렛</span>에.
              </h1>
            </Reveal>

            <Reveal variant="up" delay={180}>
              <p className="max-w-[620px] text-[19px] [word-break:keep-all]" style={{ color: '#555' }}>
                교육 수료 이력, 자격 인증서, 워크샵 참여 기록, QR 검증 정보까지 STAMP FIT 디지털 커리어
                월렛에서 체계적으로 관리하고 증명하세요.
              </p>
            </Reveal>
          </div>

          {/* 우측 KEY FEATURES 패널 */}
          <Reveal variant="right" delay={120}>
            <div
              className="relative overflow-hidden rounded-[36px] p-[30px]"
              style={{
                background:
                  'radial-gradient(circle at top right, rgba(227,59,59,0.18), transparent 34%), linear-gradient(145deg, rgba(255,255,255,0.96), rgba(255,246,246,0.92))',
                border: '1px solid rgba(227,59,59,0.12)',
                boxShadow: '0 30px 80px rgba(227,59,59,0.12)',
              }}
            >
              {/* 우하단 원형 장식 */}
              <span
                className="pointer-events-none absolute -bottom-[70px] -right-[70px] h-[180px] w-[180px] rounded-full"
                style={{ background: 'rgba(227,59,59,0.12)' }}
              />

              <div className="relative mb-[22px]">
                <strong
                  className="inline-flex rounded-full px-3 py-[7px] text-[12px] text-white"
                  style={{ background: '#111', letterSpacing: '0.02em' }}
                >
                  KEY FEATURES
                </strong>
              </div>

              <div className="relative grid grid-cols-1 gap-3.5 sm:grid-cols-2">
                {FEATURES.map((f, i) => (
                  <Reveal
                    key={f.num}
                    variant="up"
                    delay={200 + i * 110}
                    className="rounded-[24px] p-[22px] transition duration-[250ms] hover:-translate-y-1"
                    style={{
                      background: 'rgba(255,255,255,0.82)',
                      border: '1px solid rgba(227,59,59,0.1)',
                      boxShadow: '0 14px 34px rgba(0,0,0,0.05)',
                    }}
                  >
                    <div
                      className="mb-4 flex h-[34px] w-[34px] items-center justify-center rounded-[12px] text-[13px] font-black"
                      style={{ background: '#fff1f1', color: RED }}
                    >
                      {f.num}
                    </div>
                    <h3 className="mb-2 text-[18px]" style={{ letterSpacing: '-0.04em' }}>
                      {f.title}
                    </h3>
                    <p className="text-[14px]" style={{ color: '#666' }}>
                      {f.desc}
                    </p>
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ===== CTA 헤더 밴드 (다크) — 아래 인증서 보관함까지 다크 배경 연장 ===== */}
      <section
        className="relative overflow-hidden pb-[88px] pt-[80px] text-white"
        style={{
          background:
            'radial-gradient(circle at bottom right, rgba(227,59,59,0.18), transparent 34%), #111',
        }}
      >
        {/* 펄스 글로우 */}
        <div
          className="wl-glow pointer-events-none absolute -right-[80px] bottom-[-80px] h-[320px] w-[320px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(227,59,59,0.25), transparent 60%)' }}
        />
        <Reveal variant="up" className="relative mx-auto w-[min(1120px,100%)] text-center">
          <h2
            className="mb-[18px] font-black"
            style={{
              fontSize: 'clamp(36px, 5vw, 64px)',
              lineHeight: 1.12,
              letterSpacing: '-0.07em',
            }}
          >
            MY DIGITAL CAREER WALLET
          </h2>
          <p className="text-[18px]" style={{ color: 'rgba(255,255,255,0.65)' }}>
            아래에서 내 인증서를 확인하고 관리하세요.
          </p>
        </Reveal>

        {/* 인증서 보관함 — 같은 다크 배경 위에 이어서 렌더 */}
        {children && (
          <div className="relative z-[1] mx-auto mt-16 w-[min(1180px,100%)]">{children}</div>
        )}
      </section>
    </div>
  );
}
